import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpsertCustomer } from '../../hooks/useCustomers';
import { useAuthStore } from '../../stores/auth.store';
import type { Customer } from '@motra/shared';

interface CmField {
  key: string;
  label: string;
  type?: string;
  required?: boolean;
}

interface CustomerFormProps {
  customer: Customer | null;
  cmFields: CmField[];
  onSaved: () => void;
  onCancel: () => void;
}

export default function CustomerForm({ customer, cmFields, onSaved, onCancel }: CustomerFormProps) {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const upsert = useUpsertCustomer();

  // Initialize metadata from existing customer
  const initialMeta: Record<string, string> = {};
  if (customer?.metadata) {
    try {
      const parsed = JSON.parse(customer.metadata);
      Object.assign(initialMeta, parsed);
    } catch { /* empty */ }
  }

  const [fieldValues, setFieldValues] = useState<Record<string, string>>(initialMeta);
  const [discount, setDiscount] = useState<number>(customer?.discount ?? 0);
  const [creditPin, setCreditPin] = useState('');
  const [error, setError] = useState('');

  function setField(key: string, value: string) {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    try {
      await upsert.mutateAsync({
        ...(customer ? { id: customer.id } : {}),
        shopid: shop?.id,
        discount,
        credit_pin: creditPin || undefined,
        metadata: JSON.stringify(fieldValues),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="customer-form">
      {/* Dynamic fields from cm_field config */}
      {cmFields.map((field) => (
        <div key={field.key} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="text-danger"> *</span>}
          </label>
          {field.type === 'textarea' ? (
            <textarea
              className="form-textarea"
              value={fieldValues[field.key] ?? ''}
              onChange={(e) => setField(field.key, e.target.value)}
              required={field.required}
              rows={3}
            />
          ) : field.type === 'select' ? (
            <select
              className="form-select"
              value={fieldValues[field.key] ?? ''}
              onChange={(e) => setField(field.key, e.target.value)}
              required={field.required}
            >
              <option value="">— Select —</option>
            </select>
          ) : (
            <input
              type={field.type ?? 'text'}
              className="form-input"
              value={fieldValues[field.key] ?? ''}
              onChange={(e) => setField(field.key, e.target.value)}
              required={field.required}
            />
          )}
        </div>
      ))}

      {/* Discount */}
      <div className="form-group">
        <label className="form-label">{t('discount', 'Default Discount')} (%)</label>
        <input
          type="number"
          className="form-input"
          value={discount}
          onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
          min={0}
          max={100}
          step="0.5"
        />
      </div>

      {/* Credit PIN (only when creating) */}
      {!customer && (
        <div className="form-group">
          <label className="form-label">{t('credit_pin', 'Credit PIN')}</label>
          <input
            type="password"
            className="form-input"
            value={creditPin}
            onChange={(e) => setCreditPin(e.target.value)}
            placeholder="4-6 digit PIN"
            maxLength={6}
          />
        </div>
      )}

      {error && <div className="alert alert--error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="btn btn--outline" onClick={onCancel}>
          {t('cancel', 'Cancel')}
        </button>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={upsert.isPending}
        >
          {upsert.isPending ? 'Saving...' : t('save', 'Save')}
        </button>
      </div>
    </form>
  );
}
